"use client";
import { useEffect, useRef, useState } from "react";
import { useTheme } from "../../context/ThemeContext";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "../../firebase.config";
import "../../styles/df-messenger-custom.css";
import useAuth from "../../hooks/useAuth";
import toast from "react-hot-toast";
import LongInputModal from "./longInputModal";
import axios from "axios";
import OnboardingNotification from "./OnboardingNotification";

export default function ChatBox({
  instant = false,
  setAssignments,
  assignments,
}) {
  const [user_id, setUserId] = useState(null);
  const [user_name, setUserName] = useState(null);
  const [routine_date, setRoutineDate] = useState(null);
  const [course_day, setCourseDay] = useState(0);
  const [weight, setWeight] = useState(0);
  const [routine, setRoutine] = useState(null);
  const [dailyrouting, setDailyrouting] = useState(true);
  const [video_flag, setVideoFlag] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [video_counter, setVideoCounter] = useState(0);
  const [currentAgentId, setCurrentAgentId] = useState("");
  const [diffDays, setDiffDays] = useState(null);
  const [hasPaid, setIsPlanPaid] = useState(true);

  // Long input modal state
  const [isOpen, setIsOpen] = useState(false);
  const [assignmentReply, setAssignmentReply] = useState("");

  const messengerRef = useRef();
  const videos = [];
  const videoFlagRef = useRef(video_flag);
  const [isUserDataReady, setIsUserDataReady] = useState(false);
  const [userData, setUserData] = useState({});
  const [isBetween6AMand4PM, setIsBetween6AMand4PM] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isSubmittingAssignment, setIsSubmittingAssignment] = useState(false);
  const [isSessionReady, setIsSessionReady] = useState(false);
  const [onboarding, setOnboarding] = useState({});
  const lastFailedMessage = useRef(null);
  const storageOverrideRef = useRef(null);
  const { user, logout, loading } = useAuth();
  const { theme } = useTheme();

  // Track if messenger is loaded and if initial query was sent
  const [isMessengerLoaded, setIsMessengerLoaded] = useState(false);
  const hasInitialQuerySent = useRef(false);
  const isInitializing = useRef(false);

  // Reference to track if SVG has been set for mobile
  const isMobileSVGSetRef = useRef(false);
  // Store the original SVG for restoration
  const originalSVGRef = useRef(null);

  // Long input modal handlers
  const handleLongInputClose = async () => {
    setIsOpen(false);
    if (user_id) {
      try {
        await updateDoc(doc(db, "users", user_id), {
          longInputEnabled: false,
        });
        console.log("longInputEnabled reset to false in DB");
      } catch (error) {
        console.error("Error resetting longInputEnabled:", error);
      }
    }
  };

  const handleLongInputSubmit = async (assignment) => {
    setIsSubmittingAssignment(true);
    try {
      const result = await axios.post(
        "https://us-central1-rise-lean-uat.cloudfunctions.net/assignmentReplyHttp",
        {
          userId: user_id,
          courseDay: course_day,
          fileContent: assignment,
        }
      );
      console.log("Long input submitted successfully:", result.data);
      setAssignmentReply(result.data.reply);
      toast.success("Assignment submitted successfully 🎉");
      handleLongInputClose();
    } catch (error) {
      console.error("Error submitting long input:", error);
      toast.error("Failed to submit assignment. Please try again ❌");
    } finally {
      setIsSubmittingAssignment(false);
    }
  };

  const convertToEmbedUrl = (url) => {
    const match = url.match(/[?&]v=([^&]+)/);
    if (match && match[1]) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
    return null;
  };

  const getSelectedAgentId = (instant, course_day) => {
    const instantAgentID = process.env.REACT_APP_INSTANT_AGENT_ID;
    const defaultAgentID =
      course_day <= 20
        ? process.env.REACT_APP_DEFAULT_AGENT_ID
        : process.env.REACT_APP_SECOND_DEFAULT_AGENT_ID;
    return instant ? instantAgentID : defaultAgentID;
  };

  // LocalStorage guard for daily chat auto "Hi"
  const INITIAL_GREETING_KEY = "df-messenger-initial-hi-sent";

  const hasInitialGreetingBeenSent = () => {
    try {
      return localStorage.getItem(INITIAL_GREETING_KEY) === "true";
    } catch {
      return false;
    }
  };

  const markInitialGreetingAsSent = () => {
    try {
      localStorage.setItem(INITIAL_GREETING_KEY, "true");
    } catch {
      // ignore
    }
  };

  // Storage override for instant chat (only prefixes instant keys, never touches main chat keys)
  useEffect(() => {
    if (instant) {
      const original = {
        setItem: Storage.prototype.setItem,
        getItem: Storage.prototype.getItem,
        removeItem: Storage.prototype.removeItem,
        key: Storage.prototype.key,
        clear: Storage.prototype.clear,
      };

      Storage.prototype.setItem = function (key, value) {
        if (
          key &&
          (key.includes("f-messenger-messages") ||
            key.includes("df-messenger-messages") ||
            key.includes("df-messenger-welcomeIntentTriggered") ||
            key.includes("df-messenger-sessionID") ||
            key.includes("df-messenger-chips") ||
            key.includes("df-messenger-chatScrollDistance") ||
            key.includes("df-messenger-lastResponse"))
        ) {
          const currentSessionId = sessionId;
          if (currentSessionId && currentSessionId.startsWith("instant_")) {
            const instantKey = `instant_${key}`;
            return original.setItem.call(this, instantKey, value);
          }
        }
        return original.setItem.call(this, key, value);
      };

      Storage.prototype.getItem = function (key) {
        if (
          key &&
          (key.includes("f-messenger-messages") ||
            key.includes("df-messenger-messages") ||
            key.includes("df-messenger-welcomeIntentTriggered") ||
            key.includes("df-messenger-sessionID") ||
            key.includes("df-messenger-chips") ||
            key.includes("df-messenger-chatScrollDistance") ||
            key.includes("df-messenger-lastResponse"))
        ) {
          const currentSessionId = sessionId;
          if (currentSessionId && currentSessionId.startsWith("instant_")) {
            const instantKey = `instant_${key}`;
            const value = original.getItem.call(this, instantKey);
            if (value !== null) {
              return value;
            }
            // brand-new instant session: start empty without touching main keys
            return null;
          }
        }
        // non-instant or non-df-messenger keys behave normally
        return original.getItem.call(this, key);
      };

      Storage.prototype.removeItem = function (key) {
        if (
          key &&
          (key.includes("f-messenger-messages") ||
            key.includes("df-messenger-messages") ||
            key.includes("df-messenger-welcomeIntentTriggered") ||
            key.includes("df-messenger-sessionID") ||
            key.includes("df-messenger-chips") ||
            key.includes("df-messenger-chatScrollDistance") ||
            key.includes("df-messenger-lastResponse"))
        ) {
          const currentSessionId = sessionId;
          if (currentSessionId && currentSessionId.startsWith("instant_")) {
            const instantKey = `instant_${key}`;
            return original.removeItem.call(this, instantKey);
          }
        }
        return original.removeItem.call(this, key);
      };

      storageOverrideRef.current = { original };
      return () => {
        if (storageOverrideRef.current) {
          const { original: originalMethods } = storageOverrideRef.current;
          Storage.prototype.setItem = originalMethods.setItem;
          Storage.prototype.getItem = originalMethods.getItem;
          Storage.prototype.removeItem = originalMethods.removeItem;
          Storage.prototype.key = originalMethods.key;
          Storage.prototype.clear = originalMethods.clear;
          storageOverrideRef.current = null;
        }
      };
    }

    const handleDfMessengerEvents = (event) => {
      if (event.detail && event.detail.event === "WELCOME") {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    document.addEventListener(
      "df-messenger-send-event",
      handleDfMessengerEvents,
      true
    );
    return () => {
      document.removeEventListener(
        "df-messenger-send-event",
        handleDfMessengerEvents,
        true
      );
    };
  }, [instant, sessionId, user_id]);

  // Set session ID
  useEffect(() => {
    if (user_id) {
      if (instant) {
        // instant: check for existing session or create new one
        let instantSessionId = localStorage.getItem(
          `instant_session_${user_id}`,
        );
        if (!instantSessionId) {
          instantSessionId = `instant_${user_id}_${Date.now()}`;
          localStorage.setItem(`instant_session_${user_id}`, instantSessionId);
        }
        setSessionId(instantSessionId);
        setIsSessionReady(true);
      } else {
        // main window chat: stable session id so history persists
        const persistentSessionId = `${user_id}_day_${course_day}`;
        setSessionId(persistentSessionId);
        setIsSessionReady(true);
      }
    }
  }, [user_id, instant, course_day]);

  // Listen for df-messenger-loaded event
  useEffect(() => {
    let pollId;
    let pollTimeoutId;

    const handleMessengerLoaded = () => {
      console.log("✅ df-messenger loaded (event)");
      setIsMessengerLoaded(true);
      if (pollId) clearInterval(pollId);
      if (pollTimeoutId) clearTimeout(pollTimeoutId);
    };

    window.addEventListener("df-messenger-loaded", handleMessengerLoaded);

    // Fallback: poll to detect if df-messenger is already initialized.
    // On re-login the script is already loaded so the custom element may
    // fire its "df-messenger-loaded" event before this listener is
    // registered, causing the initial greeting to never be sent.
    pollId = setInterval(() => {
      const dfMessenger = document.querySelector("df-messenger");
      if (dfMessenger && typeof dfMessenger.sendQuery === "function") {
        console.log("✅ df-messenger already loaded (polling fallback)");
        setIsMessengerLoaded(true);
        clearInterval(pollId);
        if (pollTimeoutId) clearTimeout(pollTimeoutId);
      }
    }, 300);

    // Stop polling after 10 seconds to avoid infinite polling
    pollTimeoutId = setTimeout(() => {
      if (pollId) clearInterval(pollId);
    }, 10000);

    return () => {
      window.removeEventListener("df-messenger-loaded", handleMessengerLoaded);
      if (pollId) clearInterval(pollId);
      if (pollTimeoutId) clearTimeout(pollTimeoutId);
    };
  }, []);

  // Send initial query when messenger is loaded and data is ready
  useEffect(() => {
    if (isInitializing.current) {
      return;
    }

    if (
      !isMessengerLoaded ||
      !isSessionReady ||
      !user_id ||
      user_name === null ||
      hasInitialQuerySent.current ||
      (!instant && hasInitialGreetingBeenSent())
    ) {
      return;
    }

    if (!instant && course_day === 1 && routine === "morning") {
      if (!isBetween6AMand4PM) {
        return;
      }
    }

    const sendInitialHi = () => {
      const dfMessenger = document.querySelector("df-messenger");
      if (!dfMessenger || typeof dfMessenger.sendQuery !== "function") {
        return false;
      }

      isInitializing.current = true;

      // instant: clear only this df-messenger storage (main chat unaffected)
      // if (instant && typeof dfMessenger.clearStorage === "function") {
      //   dfMessenger.clearStorage();
      // }

      const now = new Date();
      const system_time = now.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      const system_datetime = now.toLocaleString([], {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });

      dfMessenger.setQueryParameters({
        parameters: {
          user_name: user_name || "Guest",
          user_id: user_id,
          routine_date: routine_date,
          course_day: course_day,
          weight: weight,
          routine: routine,
          dailyrouting: dailyrouting,
          system_time: system_time,
          system_datetime: system_datetime,
          video_counter: video_counter,
          video_flag: video_flag,
          token: authToken,
          onboardingData: onboarding,
          assignments: assignments,
          session_type: instant ? "instant_support" : "daily_chat",
        },
      });

      dfMessenger.sendQuery("Hi");
      hasInitialQuerySent.current = true;
      if (!instant) {
        markInitialGreetingAsSent();
      }
      console.log(`✅ Initial query sent to ${instant ? "instant" : "regular"} chat`);

      setTimeout(() => {
        isInitializing.current = false;
      }, 1000);

      return true;
    };

    // Try sending immediately; if df-messenger isn't ready yet, retry
    if (!sendInitialHi()) {
      console.log("⏳ df-messenger not ready, retrying initial query...");
      let retryCount = 0;
      const retryInterval = setInterval(() => {
        retryCount++;
        if (sendInitialHi() || retryCount >= 10) {
          clearInterval(retryInterval);
          if (retryCount >= 10) {
            console.warn("❌ Failed to send initial query after 10 retries");
          }
        }
      }, 500);

      return () => clearInterval(retryInterval);
    }
  }, [
    isMessengerLoaded,
    isSessionReady,
    user_id,
    user_name,
    instant,
    course_day,
    routine,
    isBetween6AMand4PM,
    routine_date,
    weight,
    dailyrouting,
    video_counter,
    video_flag,
    authToken,
    onboarding,
    assignments,
  ]);

  // Update parameters when data changes (for subsequent interactions)
  useEffect(() => {
    if (!isMessengerLoaded || !user_id || user_name === null) return;

    const dfMessenger = document.querySelector("df-messenger");
    if (!dfMessenger) return;

    const now = new Date();
    const system_time = now.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const system_datetime = now.toLocaleString([], {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });

    dfMessenger.setQueryParameters({
      parameters: {
        user_name: user_name || "Guest",
        user_id: user_id,
        routine_date: routine_date,
        course_day: course_day,
        weight: weight,
        routine: routine,
        dailyrouting: dailyrouting,
        system_time: system_time,
        system_datetime: system_datetime,
        video_counter: video_counter,
        video_flag: video_flag,
        token: authToken,
        onboardingData: onboarding,
        assignments: assignments,
      },
    });
  }, [
    isMessengerLoaded,
    user_id,
    user_name,
    routine_date,
    course_day,
    weight,
    routine,
    dailyrouting,
    video_counter,
    video_flag,
    authToken,
    onboarding,
    assignments,
  ]);

  // Scheduled Hi messages at 6 AM and 8 PM
  useEffect(() => {
    const checkTime = () => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const seconds = now.getSeconds();

      if (seconds === 0) {
        const isTargetTime = minutes === 0 && (hours === 6 || hours === 20);

        if (isTargetTime) {
          const dfMessenger = document.querySelector("df-messenger");
          if (dfMessenger && typeof dfMessenger.sendQuery === "function" && !instant) {
            dfMessenger.sendQuery("Hi");
            console.log("Triggered Hi:", now.toLocaleTimeString());
          }
        }
      }
    };

    const interval = setInterval(checkTime, 1000);
    return () => clearInterval(interval);
  }, [instant]);

  // Check if current time is between 6 AM and 4 PM
  useEffect(() => {
    const checkBetween6AMand4PM = () => {
      const now = new Date();
      const currentHour = now.getHours();
      const isBetweenHours = currentHour >= 6 && currentHour < 16;
      setIsBetween6AMand4PM(isBetweenHours);
    };

    checkBetween6AMand4PM();
    const interval = setInterval(checkBetween6AMand4PM, 60000);
    return () => clearInterval(interval);
  }, []);

  // Apply theme and styles
  useEffect(() => {
    const applyThemeAndStyles = () => {
      const dfMessenger = document.querySelector("df-messenger");
      if (!dfMessenger) return false;

      if (theme === "dark") {
        dfMessenger.setAttribute("data-theme", "dark");
      } else {
        dfMessenger.removeAttribute("data-theme");
      }

      try {
        const chat = dfMessenger.querySelector("df-messenger-chat");
        if (!chat?.shadowRoot) return false;

        const chatRoot1 = chat.shadowRoot.querySelector("df-messenger-user-input");
        if (!chatRoot1?.shadowRoot) return false;

        const inputParentWrapper = chatRoot1.shadowRoot.querySelector(".input-box-wrapper");
        if (!inputParentWrapper) return false;

        inputParentWrapper.style.backgroundColor = theme === "dark" ? "#0f1115" : "#FFFFFF";
        inputParentWrapper.style.border = theme === "dark" ? "1px solid #232733" : "1px solid #E6E6EA";

        return true;
      } catch (error) {
        return false;
      }
    };

    let attempts = 0;
    const maxAttempts = 5;

    const tryApply = () => {
      if (applyThemeAndStyles() || attempts >= maxAttempts) {
        return;
      }

      attempts++;
      setTimeout(tryApply, attempts * 200);
    };

    tryApply();
  }, [theme]);

  // Handle assignment reply
  useEffect(() => {
    if (!assignmentReply) return;

    const dfMessenger = document.querySelector("df-messenger");
    if (!dfMessenger) return;

    const sendMessage = () => {
      if (typeof dfMessenger.renderCustomText === "function") {
        dfMessenger.setQueryParameters({
          parameters: { assignment_reply: assignmentReply },
        });
        dfMessenger.sendRequest("event", "ASSIGNMENT_REPLY_TRIGGER");
      } else if (typeof dfMessenger.sendQuery === "function") {
        dfMessenger.setQueryParameters({
          parameters: { assignment_reply: assignmentReply },
        });
        dfMessenger.sendRequest("event", "ASSIGNMENT_REPLY_TRIGGER");
      } else {
        dfMessenger.setQueryParameters({
          parameters: { assignment_submission: false },
        });
        dfMessenger.sendRequest("event", "ASSIGNMENT_REPLY_TRIGGER");
      }
    };

    sendMessage();
  }, [assignmentReply]);

  // Apply custom styles (unchanged)
  useEffect(() => {
    let interval;
    let isIntervalCleared = false;
    const applyCustomStyles = () => {
      const dfMessenger = document.querySelector("df-messenger");
      const chat = dfMessenger?.querySelector("df-messenger-chat");
      const chatRoot1 = chat?.shadowRoot?.querySelector("df-messenger-user-input");
      const titleBar = chat?.shadowRoot?.querySelector("df-messenger-titlebar");
      const messageList = chat?.shadowRoot?.querySelector("df-messenger-message-list");
      const contentDiv = messageList?.shadowRoot.querySelector("#message-list .content");
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

      if (isIOS) {
        const input = chatRoot1?.shadowRoot?.querySelector("textarea");
        if (input) {
          input.style.fontSize = "16px";
          input.addEventListener("blur", () => {
            setTimeout(() => {
              window.scrollTo(0, 0);
              document.body.style.transform = "scale(1)";
              document.body.style.transformOrigin = "top";
              setTimeout(() => {
                document.body.style.transform = "";
                document.body.style.transformOrigin = "";
              }, 100);
            }, 100);
          });
        }
      }

      if (messageList) {
        messageList.style.flexGrow = "0";
        const messageLisWrapper = messageList?.shadowRoot?.querySelector(".message-list-wrapper");
        if (messageLisWrapper && messageLisWrapper.classList.contains("show-shadow")) {
          messageLisWrapper.classList.remove("show-shadow");
        }
      }

      if (contentDiv) {
        contentDiv.querySelectorAll(".entry.bot").forEach((entry) => {
          const utterance = entry?.querySelector("df-messenger-utterance");
          if (!utterance?.shadowRoot) return;

          // Find all df-chips or chip containers in the utterance shadow root
          utterance.shadowRoot.querySelectorAll("df-chips").forEach((dfChips) => {
            if (!dfChips?.shadowRoot) return;
            // Inject style only once per shadow root
            if (!dfChips.shadowRoot.querySelector("#chip-fix-style")) {
              const style = document.createElement("style");
              style.id = "chip-fix-style";
              style.textContent = `
                .chip {
                  padding: 0 !important;
                  cursor: pointer !important;
                }
                .chip button.focus-outline {
                  width: 100% !important;
                  height: 100% !important;
                  padding: 6px 16px !important;
                  margin: 0 !important;
                  cursor: pointer !important;
                  box-sizing: border-box !important;
                  display: flex !important;
                  align-items: center !important;
                  justify-content: center !important;
                  border: none !important;
                  background: transparent !important;
                  color: inherit !important;
                  font: inherit !important;
                }
              `;
              dfChips.shadowRoot.appendChild(style);
            }
          });
        });
      }


      if (contentDiv) {
        contentDiv.querySelectorAll(".entry.bot").forEach((entry) => {
          const hrefEntry = entry?.querySelector("df-messenger-utterance");
          hrefEntry?.shadowRoot?.querySelectorAll(".message-stack").forEach((utterance) => {
            const markdown = utterance?.querySelector("df-markdown-message");
            const aTag = markdown?.shadowRoot?.querySelector("a");
            if (aTag) {
              const href = aTag.getAttribute("href");
              if (href && href.match(/\.(mp4|webm|ogg)(\?.*)?$/i)) {
                const video = document.createElement("video");
                video.src = href;
                video.controls = true;
                video.setAttribute("controlsList", "nodownload");
                video.disablePictureInPicture = true;
                video.style.width = "100%";
                video.style.maxHeight = "400px";
                video.style.borderRadius = "8px";
                aTag.replaceWith(video);
                videos.push(video);
              }
            }
          });

          const actionButtonWrapper = entry?.querySelector("df-messenger-utterance")?.shadowRoot
            ?.querySelector("df-card")?.shadowRoot?.querySelector(".card-wrapper");
          const dfButtonEl = actionButtonWrapper?.querySelector("df-button");
          const actionButton = dfButtonEl?.shadowRoot
            ?.querySelector("#dfButtonAnchorWrapper");

          if (actionButton) {
            actionButton.style.backgroundColor = theme === "dark" ? "#1B1F27" : "#F4F4F6";
            actionButton.style.borderColor = theme === "dark" ? "#1B1F27" : "#F4F4F6";
            actionButton.style.display = "block";
            actionButton.style.width = "100%";
            actionButton.style.boxSizing = "border-box";
            actionButton.style.textAlign = "center";
            actionButton.style.cursor = "pointer";
            actionButton.style.padding = "12px 16px";
          }

          // Ensure the df-button host element itself is full-width clickable
          if (dfButtonEl) {
            dfButtonEl.style.display = "block";
            dfButtonEl.style.width = "100%";
            dfButtonEl.style.cursor = "pointer";
          }

          const dfVideo = entry?.querySelector("df-messenger-utterance")?.shadowRoot
            ?.querySelector("df-card")?.shadowRoot?.querySelector("df-video");
          const wrapper = dfVideo?.shadowRoot?.querySelector?.(".wrapper");
          const anchor = wrapper?.querySelector("a");

          if (anchor) {
            const OriginalvideoUrl = anchor?.getAttribute("href");
            const embedUrl = OriginalvideoUrl.includes("youtube.com/watch")
              ? convertToEmbedUrl(OriginalvideoUrl)
              : OriginalvideoUrl;

            if (embedUrl) {
              const iframe = document.createElement("iframe");
              iframe.src = embedUrl;
              iframe.width = "100%";
              iframe.height = "200";
              iframe.allowFullscreen = true;
              iframe.frameBorder = 0;
              wrapper.replaceChild(iframe, anchor);
            }
          }
        });
      }

      if (!chatRoot1) return;

      chatRoot1.style.width = "90%";
      chatRoot1.style.margin = "0 auto";

      const chatRoot = chatRoot1.shadowRoot;
      if (!chatRoot) return;

      const inputWrapper = chatRoot.querySelector(".input-element-wrapper");
      const inputParentWrapper = chatRoot.querySelector(".input-box-wrapper");
      const inputField = chatRoot.querySelector("input");
      const sendButton = chatRoot.querySelector("#send-icon-button");
      const textarea = inputParentWrapper?.querySelector("textarea.input-box");

      if (inputWrapper && sendButton && inputField && titleBar) {
        if (interval) {
          clearInterval(interval);
          isIntervalCleared = true;
          console.log("✅ Interval cleared - elements loaded");
        }
      }

      if (inputParentWrapper) {
        inputParentWrapper.style.display = "flex";
        inputParentWrapper.style.alignItems = "center";
        inputParentWrapper.style.overflow = "hidden";
        inputParentWrapper.style.borderRadius = "2rem";
        inputParentWrapper.style.paddingLeft = "12px";
        inputParentWrapper.style.border = "2px solid #E6E6EA";
        inputParentWrapper.style.gap = "8px";
      }

      if (inputWrapper) {
        inputWrapper.style.flex = "1";
        inputWrapper.style.marginLeft = "0px";
        inputWrapper.style.marginRight = "0px";
        inputWrapper.style.border = "none";
        inputWrapper.style.display = "flex";
        inputWrapper.style.alignItems = "center";
      }

      if (textarea) {
        textarea.style.width = "100%";
        textarea.style.flex = "1";
        textarea.style.border = "none";
        textarea.style.outline = "none";
        textarea.style.resize = "none";
      }

      const existingLabel = sendButton?.querySelector(".custom-send-label");

      if (window.innerWidth > 481) {
        if (sendButton) {
          if (isMobileSVGSetRef.current) {
            const customMobileSVG = sendButton.querySelector(".custom-mobile-send-icon");
            if (customMobileSVG) {
              customMobileSVG.remove();
            }

            if (originalSVGRef.current) {
              sendButton.appendChild(originalSVGRef.current.cloneNode(true));
            }

            isMobileSVGSetRef.current = false;
          }

          sendButton.style.display = "flex";
          sendButton.style.alignItems = "center";
          sendButton.style.justifyContent = "center";
          sendButton.style.borderRadius = "5rem";
          sendButton.style.width = "7rem";
          sendButton.style.height = "3rem";
          sendButton.style.backgroundColor = theme === "dark" ? "#069c27" : "#069c27";
          sendButton.style.border = `2px solid ${theme === "dark" ? "#069c27" : "#069c27"}`;
          sendButton.style.marginRight = "0px";
          sendButton.style.marginTop = "-.3rem";
          sendButton.style.marginBottom = "0px";
          sendButton.style.padding = "6px 8px";
          sendButton.style.cursor = "pointer";
          sendButton.style.transition = "all 0.2s ease";
          sendButton.style.flexShrink = "0";
          sendButton.style.position = "relative";

          if (!existingLabel) {
            const existingSVG = sendButton.querySelector("svg");
            let svgClone = null;

            if (existingSVG) {
              svgClone = existingSVG.cloneNode(true);
              svgClone.style.maxHeight = "1.5rem";
              svgClone.style.maxWidth = "1.5rem";
              svgClone.style.flexShrink = "0";
              svgClone.style.marginRight = "1.7rem";
            }

            const sendText = document.createElement("span");
            sendText.className = "custom-send-label";
            sendText.textContent = "Send";
            sendText.style.color = "#ffffff";
            sendText.style.fontWeight = "600";
            sendText.style.fontSize = "14px";
            sendText.style.lineHeight = "1";
            sendText.style.flexShrink = "0";
            sendText.style.paddingLeft = "1.7rem";

            sendButton.innerHTML = "";
            sendButton.appendChild(sendText);

            if (svgClone) {
              sendButton.appendChild(svgClone);
            }
          }
          sendButton.style.marginRight = "1.5rem";
        }
      } else {
        if (existingLabel) {
          existingLabel.remove();
        }

        const isAlreadyMobile = sendButton?.style.width === "36px" && sendButton?.style.height === "36px";

        if (sendButton && !isAlreadyMobile) {
          sendButton.removeAttribute("style");

          sendButton.style.backgroundColor = theme === "dark" ? "#2E8C50" : "#205072";
          sendButton.style.borderRadius = "50%";
          sendButton.style.width = "36px";
          sendButton.style.height = "36px";
          sendButton.style.display = "flex";
          sendButton.style.alignItems = "center";
          sendButton.style.justifyContent = "center";
          sendButton.style.position = "relative";
          sendButton.style.border = "none";
          sendButton.style.marginRight = "0px";
          sendButton.style.marginBottom = "0px";
          sendButton.style.padding = "0px";
          sendButton.style.flexShrink = "0";

          if (!isMobileSVGSetRef.current) {
            const originalSVG = sendButton.querySelector("svg");
            if (originalSVG && !originalSVGRef.current) {
              originalSVGRef.current = originalSVG.cloneNode(true);
            }

            sendButton.querySelectorAll("svg").forEach((svg) => svg.remove());
            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(
              `<svg xmlns="http://www.w3.org/2000/svg" fill="#ffffff" width="16" height="16" viewBox="0 0 495.003 495.003">
          <g>
            <path d="M164.711,456.687c0,2.966,1.647,5.686,4.266,7.072c2.617,1.385,5.799,1.207,8.245-0.468l55.09-37.616 l-67.6-32.22V456.687z"/>
            <path d="M492.431,32.443c-1.513-1.395-3.466-2.125-5.44-2.125c-1.19,0-2.377,0.264-3.5,0.816L7.905,264.422 c-4.861,2.389-7.937,7.353-7.904,12.783c0.033,5.423,3.161,10.353,8.057,12.689l125.342,59.724l250.62-205.99L164.455,364.414 l156.145,74.4c1.918,0.919,4.012,1.376,6.084,1.376c1.768,0,3.519-0.322,5.186-0.977c3.637-1.438,6.527-4.318,7.97-7.956 L494.436,41.257C495.66,38.188,494.862,34.679,492.431,32.443z"/>
          </g>
        </svg>`,
              "image/svg+xml"
            );

            const newSVG = svgDoc.documentElement;
            newSVG.classList.add("custom-mobile-send-icon");
            sendButton.appendChild(newSVG);
            isMobileSVGSetRef.current = true;
          }
        }
      }

      if (titleBar) {
        titleBar.style.visibility = "hidden";
        titleBar.style.height = "0";
        titleBar.style.padding = "0";
        titleBar.style.margin = "0";
      }

      // Disable send button if input is too long (> 256 characters)
      const updateSendButtonState = () => {
        if (textarea && sendButton) {
          if (textarea.value.length > 255) {
            sendButton.style.opacity = "0.5";
            sendButton.style.pointerEvents = "none";
            sendButton.style.cursor = "not-allowed";
          } else {
            sendButton.style.opacity = "1";
            sendButton.style.pointerEvents = "auto";
            sendButton.style.cursor = "pointer";
          }
        }
      };

      if (textarea && !textarea.dataset.listenerAdded) {
        textarea.addEventListener("input", updateSendButtonState);
        textarea.dataset.listenerAdded = "true";
      }

      updateSendButtonState();

      if (inputWrapper && sendButton && inputField && titleBar) {
        clearInterval(interval);
      }

      const scrollContainer = messageList?.shadowRoot.querySelector("#message-list");
      function isInViewport(el) {
        const rect = el.getBoundingClientRect();
        return rect.top < window.innerHeight && rect.bottom > 0;
      }

      if (scrollContainer) {
        scrollContainer.addEventListener("scroll", () => {
          videos.forEach((video) => {
            if (!isInViewport(video) && !video.paused) {
              video.pause();
            }
          });
        });
      }
    };

    interval = setInterval(applyCustomStyles, 100);

    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (isIntervalCleared) {
          applyCustomStyles();
        }
      }, 250);
    };

    // Re-apply chip styles when new messages arrive (after interval is cleared)
    const handleNewResponse = () => {
      // Small delay to let the DOM update with the new message
      setTimeout(applyCustomStyles, 200);
      setTimeout(applyCustomStyles, 600);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("df-response-received", handleNewResponse);

    const handleUserInput = (event) => {
      if (!navigator.onLine) {
        lastFailedMessage.current = event.detail.input;
        const dfMessenger = document.querySelector("df-messenger");
        if (dfMessenger) {
          dfMessenger.renderCustomText("Please check your internet connection and try again.");
        }
      } else {
        lastFailedMessage.current = null;
      }
    };

    const handleMessengerError = (event) => {
      const dfMessenger = document.querySelector("df-messenger");
      if (dfMessenger) {
        dfMessenger.renderCustomText("Network error. Please check your connection and try again.");
      }
    };

    const handleOnline = () => {
      if (lastFailedMessage.current && messengerRef.current) {
        messengerRef.current.sendQuery(lastFailedMessage.current);
        lastFailedMessage.current = null;
      }
    };

    window.addEventListener("df-user-input-entered", handleUserInput);
    window.addEventListener("df-messenger-error", handleMessengerError);
    window.addEventListener("online", handleOnline);

    return () => {
      if (interval) clearInterval(interval);
      if (resizeTimeout) clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("df-response-received", handleNewResponse);
      window.removeEventListener("df-user-input-entered", handleUserInput);
      window.removeEventListener("df-messenger-error", handleMessengerError);
      window.removeEventListener("online", handleOnline);
    };
  }, [theme]);

  // Disable input for unpaid users after trial
  useEffect(() => {
    const dfMessenger = document.querySelector("df-messenger");
    if (!dfMessenger) return;

    const chat = dfMessenger.querySelector("df-messenger-chat");
    const chatRoot = chat?.shadowRoot?.querySelector("df-messenger-user-input");
    const inputParentWrapper = chatRoot?.shadowRoot?.querySelector(".input-box-wrapper");
    const textarea = inputParentWrapper?.querySelector("textarea.input-box");
    const sendButton = chatRoot?.shadowRoot?.querySelector("#send-icon-button");

    if (!textarea) return;

    const preventInput = (e) => e.preventDefault();
    textarea.removeEventListener("keydown", preventInput);
    textarea.removeEventListener("paste", preventInput);

    if (diffDays > 42 && !hasPaid) {
      dfMessenger.style.cursor = "not-allowed";
      dfMessenger.style.pointerEvents = "none";

      textarea.style.opacity = "0.6";
      textarea.style.cursor = "not-allowed";
      textarea.style.caretColor = "transparent";
      textarea.placeholder = "Pay to unlock chat ...";

      textarea.addEventListener("keydown", preventInput);
      textarea.addEventListener("paste", preventInput);
    } else {
      if (sendButton) {
        sendButton.style.border = "solid";
      }
    }

    return () => {
      textarea.removeEventListener("keydown", preventInput);
      textarea.removeEventListener("paste", preventInput);
    };
  }, [diffDays, hasPaid]);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const checkRoutine = (routine, last_updated_date) => {
    const currentDate = new Date();
    const currentHour = currentDate.getHours();

    if (routine === "morning" && currentHour < 8) {
      setDailyrouting(false);
    } else if (routine === "evening" && currentHour < 20) {
      setDailyrouting(false);
    }
  };

  // Reset longInputEnabled on window close/tab close
  useEffect(() => {
    const handleUnload = () => {
      if (user_id) {
        // We use updateDoc here, though it's async and might not always complete on window close.
        // However, this is the best we can do given the current architecture.
        updateDoc(doc(db, "users", user_id), {
          longInputEnabled: false,
        }).catch((err) => console.error("Unload reset error:", err));
      }
    };

    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("pagehide", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("pagehide", handleUnload);
    };
  }, [user_id]);

  const prevValuesRef = useRef({});

  // Load user data from Firebase
  useEffect(() => {
    const authUser = JSON.parse(localStorage.getItem("authUser"));
    const authToken = authUser?.stsTokenManager?.accessToken;
    const uid = authUser?.uid;

    if (!uid || !authToken) {
      console.warn("No user found in localStorage.");
      return;
    }

    setUserId(uid);
    setAuthToken(authToken);

    const userUnsubscribe = onSnapshot(
      doc(db, "users", uid),
      (userDoc) => {
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log("Real-time user data:", userData);

          if (prevValuesRef.current.profileImage !== userData?.profileImage) {
            setUserData((prev) => ({
              ...prev,
              profileImage: userData?.profileImage || null,
            }));
            prevValuesRef.current.profileImage = userData?.profileImage;
          }

          if (prevValuesRef.current.firstName !== userData?.firstName) {
            setUserName(userData?.firstName);
            prevValuesRef.current.firstName = userData?.firstName;
          }

          if (prevValuesRef.current.routineDate !== userData.routineDate) {
            setRoutineDate(userData.routineDate || "");
            prevValuesRef.current.routineDate = userData.routineDate;
          }

          if (prevValuesRef.current.routine !== userData?.routine) {
            checkRoutine(userData?.routine, userData?.updateAt);
            setRoutine(userData.routine || "");
            prevValuesRef.current.routine = userData.routine;
          }

          if (prevValuesRef.current.courseDay !== userData?.courseDay) {
            setCourseDay(userData?.courseDay || 0);
            localStorage.setItem("courseDay", userData?.courseDay || 0);
            window.dispatchEvent(new Event("courseDayUpdated"));
            prevValuesRef.current.courseDay = userData?.courseDay;
          }

          const newAssignments = JSON.stringify(userData?.assignments || []);
          if (prevValuesRef.current.assignments !== newAssignments) {
            setAssignments(userData?.assignments || []);
            prevValuesRef.current.assignments = newAssignments;
          }

          if (
            prevValuesRef.current.longInputEnabled !==
            userData?.longInputEnabled
          ) {
            setIsOpen(userData?.longInputEnabled || false);
            prevValuesRef.current.longInputEnabled = userData?.longInputEnabled;
          }

          if (prevValuesRef.current.weight !== userData?.weight) {
            setWeight(userData?.weight || 0);
            prevValuesRef.current.weight = userData?.weight;
          }

          const newOnboarding = {
            "If food were a person, who would it be? What's your relationship with them like?":
              userData.relationship || "NA",
            "How long have you felt like food has had too much power in your life?":
              userData.power || "NA",
            "What's your personal experience with emotional eating been like?":
              userData.emotional || "NA",
            "On an average day, how many times do you find yourself eating more because of emotion—not hunger?":
              userData.food_emotion || "NA",
            "What foods do you usually reach for when that happens?":
              userData.food_type || "NA",
            "Do you tend to finish everything on your plate even when you're full?":
              userData.plate_full || "NA",
            "If yes, what percentage of the time would you say that happens?":
              userData.plate_per || "NA",
            "On average, roughly how many hours per day do you spend feeling consumed by food and weight-related thoughts? This may include reading, researching, contemplating, and planning about these matters.":
              userData.hrs_per_day_on_food || "NA",
          };
          const newOnboardingStr = JSON.stringify(newOnboarding);
          if (prevValuesRef.current.onboarding !== newOnboardingStr) {
            setOnboarding(newOnboarding);
            prevValuesRef.current.onboarding = newOnboardingStr;
          }

          if (prevValuesRef.current.video_counter !== userData?.video_counter) {
            setVideoCounter(userData?.video_counter || 0);
            prevValuesRef.current.video_counter = userData?.video_counter;
          }

          if (prevValuesRef.current.video_flag !== userData?.video_flag) {
            setVideoFlag(userData?.video_flag || false);
            prevValuesRef.current.video_flag = userData?.video_flag;
          }

          const isPaid = !!userData?.planAssignedAt;
          if (prevValuesRef.current.isPaid !== isPaid) {
            setIsPlanPaid(isPaid);
            localStorage.setItem("isSelectedPlan", isPaid);
            prevValuesRef.current.isPaid = isPaid;
          }

          const joinedAtDate = userData.createdAt.toDate();
          const today = new Date();
          const diffMs = today - joinedAtDate;
          const newDiffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24) + 1);
          if (prevValuesRef.current.diffDays !== newDiffDays) {
            setDiffDays(newDiffDays);
            localStorage.setItem("diffDays", newDiffDays);
            prevValuesRef.current.diffDays = newDiffDays;
          }

          const fullName = `${userData.firstName} ${userData.lastName}`;
          if (prevValuesRef.current.fullName !== fullName) {
            localStorage.setItem("userName", fullName);
            prevValuesRef.current.fullName = fullName;
          }

          setIsUserDataReady(true);
        }
      },
      (error) => {
        console.error("Real-time user data error:", error);
      }
    );

    const existingScript = document.querySelector(
      'script[src="https://www.gstatic.com/dialogflow-console/fast/df-messenger/prod/v1/df-messenger.js"]'
    );

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = "https://www.gstatic.com/dialogflow-console/fast/df-messenger/prod/v1/df-messenger.js";
      script.async = true;
      document.body.appendChild(script);

      script.onload = () => {
        const originalConsoleError = console.error;
        console.error = function (...args) {
          const errorMessage = args.join(" ");
          if (errorMessage.includes("WELCOME") && errorMessage.includes("400")) {
            return;
          }
          return originalConsoleError.apply(console, args);
        };
      };
    }

    return () => {
      userUnsubscribe();
    };
  }, []);

  useEffect(() => {
    videoFlagRef.current = video_flag;
  }, [video_flag]);

  // Idle timer for logout
  useEffect(() => {
    let idleTimer1Min;
    let idleTimer15Min;
    let logoutTimer;

    const IDLE_TIME_1_MIN = 2 * 60 * 1000;
    const IDLE_TIME_15_MIN = 15 * 60 * 1000;

    const events = ["mousedown", "mousemove", "keypress", "scroll", "touchstart", "click"];

    const handleLogout = async () => {
      clearTimeout(idleTimer1Min);
      clearTimeout(idleTimer15Min);
      clearTimeout(logoutTimer);
      toast.error(
        "You have been logged out due to inactivity. Please log in again.",
      );
      await logout();
    };

    const showLogoutWarning = async () => {
      toast.success(
        "You have been inactive. You will be automatically logged out in 1 minute.",
        { duration: 5000 }
      );
    };

    const sendVideoTriggerMessage = () => {
      if (videoFlagRef.current) {
        const dfMessenger = document.querySelector("df-messenger");
        if (dfMessenger) {
          dfMessenger.renderCustomText("Enter Done if you completed watching the video?");
        }
      } else {
        console.log("Video not watched — skipping 15-min message.");
      }
    };

    const sendShortIdleMessage = () => {
      if (!videoFlagRef.current) {
        const dfMessenger = document.querySelector("df-messenger");
        try {
          const result = dfMessenger.sendRequest("event", "TIMEOUT");
          if (result && typeof result.then === "function") {
            result.catch((error) => {
              console.error("dfMessenger promise error:", error);
            });
          }
        } catch (error) {
          console.error("dfMessenger sync error:", error);
        }
      } else {
        console.log("Video is playing — skipping short idle message.");
      }
    };

    const resetIdleTimers = () => {
      clearTimeout(idleTimer1Min);
      clearTimeout(idleTimer15Min);
      clearTimeout(logoutTimer);

      idleTimer1Min = setTimeout(() => {
        sendShortIdleMessage();
      }, IDLE_TIME_1_MIN);

      idleTimer15Min = setTimeout(() => {
        sendVideoTriggerMessage();
      }, IDLE_TIME_15_MIN);

      logoutTimer = setTimeout(() => {
        showLogoutWarning();
        setTimeout(() => {
          handleLogout();
        }, 1 * 60 * 1000);
      }, 29 * 60 * 1000);
    };

    events.forEach((event) => {
      document.addEventListener(event, resetIdleTimers, true);
    });

    resetIdleTimers();

    return () => {
      clearTimeout(idleTimer1Min);
      clearTimeout(idleTimer15Min);
      clearTimeout(logoutTimer);
      events.forEach((event) => {
        document.removeEventListener(event, resetIdleTimers, true);
      });
    };
  }, [logout]);

  // Set current agent ID
  useEffect(() => {
    const selectedAgentId = getSelectedAgentId(instant, course_day);
    setCurrentAgentId(selectedAgentId);
  }, [instant, course_day]);
  const defaultAvatarSvg = (theme) => {
    const color = theme === "dark" ? "#cef7e2" : "#FFFFFF";
    return `
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="${color}"
    >
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 4c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6m0 14c-2.03 0-4.43-.82-6.14-2.88C7.55 15.8 9.68 15 12 15s4.45.8 6.14 2.12C16.43 19.18 14.03 20 12 20"/>
    </svg>
    `.trim();
  };
  const defaultAvatar = `data:image/svg+xml;utf8,${encodeURIComponent(
    defaultAvatarSvg(theme)
  )}`;

  return (
    <>
      <div className="flex justify-center w-full">
        {!instant && isUserDataReady && course_day === 0 && (
          <OnboardingNotification />
        )}

        <df-messenger
          ref={messengerRef}
          location="us-central1"
          project-id={process.env.REACT_APP_FIREBASE_PROJECT_ID}
          session-id={sessionId}
          agent-id={currentAgentId}
          language-code="en"
          max-query-length="-1"
          className={`mt-0 w-full px-2 sm:px-4 md:px-0 ${instant ? "md:w-[90%] lg:w-[90%] pb-2 md:pb-0" : "md:w-[60%] lg:w-[72%]"
            } ${course_day === 0 ? "pt-8" : ""
            } h-[calc(100dvh-13%)] md:h-[80vh] lg:h-[80vh] sm:h-[calc(100dvh-13%)]`}
        >
          <df-messenger-chat
            bot-actor-image="/assets/images/IMG_5314-modified.png"
            user-actor-image={userData?.profileImage || defaultAvatar}
            placeholder-text="Type here.."
            chat-title=""
          ></df-messenger-chat>
        </df-messenger>
      </div>

      <LongInputModal
        isOpen={isOpen}
        onClose={handleLongInputClose}
        onSubmit={handleLongInputSubmit}
        courseDay={course_day}
        isSubmitting={isSubmittingAssignment}
      />
    </>
  );
}
